import React, { useState } from 'react';
import {
  ChatBubbleLeftRightIcon,
  PhoneIcon,
  BuildingOfficeIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { LeadStatus } from '../../types/lead';
import { SmsCampaignResultLead } from '../../types/sms';
import StatusBadge from '../UI/StatusBadge';
import Button from '../UI/Button';
import SmsConversationPopup from '../Sms/SmsConversationPopup';

const STATUS_OPTIONS: { value: LeadStatus; label: string }[] = [
  { value: 'interested', label: 'Interested' },
  { value: 'not_interested', label: 'Not Interested' },
  { value: 'follow_up', label: 'Follow Up' },
  { value: 'app_out', label: 'App Out' },
  { value: 'app_in', label: 'App In' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'contract', label: 'Contract' },
  { value: 'funded', label: 'Funded' },
  { value: 'unqualified', label: 'Unqualified' },
  { value: 'do_not_call', label: 'Do Not Call' },
];

const SMS_STATUS_COLORS: Record<string, string> = {
  delivered: 'text-green-600',
  sent: 'text-blue-600',
  queued: 'text-slate-500',
  failed: 'text-red-600',
  undelivered: 'text-red-600',
};

const FIELD_SELECT =
  'block h-9 min-w-[10rem] rounded-lg border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-50';

function formatDateTime(iso?: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function stepLabel(step: number | null): string {
  if (step == null) return '—';
  if (step === 1) return 'Initial outreach';
  return `Follow-up ${step - 1}`;
}

interface SmsLeadResultCardProps {
  lead: SmsCampaignResultLead;
  updating?: boolean;
  onStatusChange: (status: LeadStatus, e: React.ChangeEvent<HTMLSelectElement>) => void;
  onViewDetails?: () => void;
}

const SmsLeadResultCard: React.FC<SmsLeadResultCardProps> = ({
  lead,
  updating,
  onStatusChange,
  onViewDetails,
}) => {
  const [smsOpen, setSmsOpen] = useState(false);

  return (
    <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div className="flex-1 min-w-0 space-y-3">
          <div className="flex flex-wrap items-start gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="font-semibold text-foreground truncate">{lead.fullName || '—'}</h3>
                <StatusBadge status={lead.status} type="lead" />
                {lead.subStageName && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                    {lead.subStageName}
                  </span>
                )}
              </div>
              <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                <span className="inline-flex items-center gap-1">
                  <PhoneIcon className="h-3.5 w-3.5" />
                  {lead.phoneE164 || '—'}
                </span>
                {lead.companyName && (
                  <span className="inline-flex items-center gap-1">
                    <BuildingOfficeIcon className="h-3.5 w-3.5" />
                    {lead.companyName}
                  </span>
                )}
              </div>
            </div>

            <select
              className={FIELD_SELECT}
              value={lead.status}
              disabled={updating}
              onChange={(e) => onStatusChange(e.target.value as LeadStatus, e)}
              onClick={(e) => e.stopPropagation()}
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 text-sm">
            <div className="rounded-lg bg-muted/30 px-3 py-2">
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">SMS sent</div>
              <div className="mt-0.5 font-medium text-foreground flex items-center gap-1">
                {lead.smsSent ? (
                  <>
                    <CheckCircleIcon className="h-4 w-4 text-green-600" />
                    Yes ({lead.outboundCount})
                  </>
                ) : (
                  <>
                    <XCircleIcon className="h-4 w-4 text-muted-foreground" />
                    Not yet
                  </>
                )}
              </div>
            </div>

            <div className="rounded-lg bg-muted/30 px-3 py-2">
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Last status</div>
              <div
                className={`mt-0.5 font-medium capitalize ${
                  SMS_STATUS_COLORS[lead.lastOutboundStatus || ''] || 'text-foreground'
                }`}
              >
                {lead.lastOutboundStatus || '—'}
              </div>
            </div>

            <div className="rounded-lg bg-muted/30 px-3 py-2">
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Last sent</div>
              <div className="mt-0.5 font-medium text-foreground flex items-center gap-1">
                <ClockIcon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <span className="truncate">{formatDateTime(lead.lastOutboundAt)}</span>
              </div>
              {lead.lastDeliveredAt && (
                <div className="text-[11px] text-green-600 dark:text-green-400 mt-0.5">
                  Delivered {formatDateTime(lead.lastDeliveredAt)}
                </div>
              )}
            </div>

            <div className="rounded-lg bg-muted/30 px-3 py-2">
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Reply</div>
              <div className="mt-0.5 font-medium text-foreground">
                {lead.hasReply ? formatDateTime(lead.lastInboundAt) : 'No reply'}
              </div>
            </div>

            <div className="rounded-lg bg-muted/30 px-3 py-2">
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Sequence</div>
              <div className="mt-0.5 font-medium text-foreground">{stepLabel(lead.lastOutboundStep)}</div>
            </div>

            <div className="rounded-lg bg-muted/30 px-3 py-2">
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Enrollment</div>
              <div className="mt-0.5 font-medium text-foreground capitalize">
                {lead.enrollmentStatus || '—'}
                {lead.enrollmentPauseReason && (
                  <span className="block text-[11px] text-muted-foreground normal-case">
                    {lead.enrollmentPauseReason}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 shrink-0">
          {onViewDetails && (
            <Button variant="outlined" size="sm" onClick={onViewDetails}>
              Details
            </Button>
          )}
          <Button
            size="sm"
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setSmsOpen(true);
            }}
            title="Open SMS conversation"
          >
            <ChatBubbleLeftRightIcon className="h-4 w-4 mr-1.5" />
            SMS
          </Button>
        </div>
      </div>

      <SmsConversationPopup
        open={smsOpen}
        onClose={() => setSmsOpen(false)}
        leadId={lead.id}
        leadName={lead.fullName}
        companyName={lead.companyName}
        leadPhone={lead.phoneE164}
      />
    </div>
  );
};

export default SmsLeadResultCard;
